// NTO toolkit: motion.
// The house curves and timings from the portfolio, plus small helpers that
// carry its rules (overlap don't queue, staggers with direction, a click to
// close always wins, reduced motion = fades only). See toolkit/README.md.

/** Named easings. Reuse these; don't invent new curves. */
export const EASE = {
    /** the house curve: most in-out moves, fills growing, panels closing */
    natural: "cubic-bezier(.8,0,.2,1)",
    /** panels opening, blur and colour changes */
    menuOpen: "cubic-bezier(.85,0,.15,1)",
    /** entrances drifting into place */
    slowDown: "cubic-bezier(0,0,0,1)",
    /** hover nudges, soft arrivals */
    smooth: "cubic-bezier(.5,0,0,1)",
    /** tiles and fields arriving, staggers */
    out: "cubic-bezier(.16,1,.3,1)",
    /** a snappy small overshoot: press, dent */
    springPress: "cubic-bezier(.3,1.6,.5,1)",
    /** icons turning on hover */
    springTurn: "cubic-bezier(.3,1.4,.5,1)",
    /** things leaving fast */
    exit: "cubic-bezier(.55,0,.75,.3)",
    /** a circle growing into a bar */
    barOpen: "cubic-bezier(.5,0,.12,1)",
} as const

/** Standard timings, in ms. */
export const MS = {
    /** functional level: everything in the portal stays within these */
    enter: 700,
    drift: 1160,
    fill: 800,
    exit: 360,
    close: 250,
    /** gaps between items in a stagger */
    stagger: 70,
    staggerLines: 50,
    staggerOut: 55,
    /** blur in / out */
    blurIn: 470,
    blurOut: 450,
} as const

/** "Overlap, don't queue": the next step starts at ~80% of the previous one. */
export const overlap = (ms: number, at = 0.8) => Math.round(ms * at)

export const reduced = () =>
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches

/** Direction of a stagger: the order means something (form fields come in
 *  right to left, text top to bottom, an arrow the way it points). */
export type Direction = "up" | "down" | "left" | "right"
const OFFSET: Record<Direction, [number, number]> = {
    up: [0, 1],
    down: [0, -1],
    left: [1, 0],
    right: [-1, 0],
}

type EnterOptions = {
    /** where it comes from */
    from?: Direction
    /** how far it travels, px */
    distance?: number
    delay?: number
    duration?: number
    easing?: string
}

/** One element in: drifts into place and fades. Reduced motion: fade only. */
export function enter(el: Element, o: EnterOptions = {}) {
    const { from = "up", distance = 24, delay = 0, duration = MS.enter, easing = EASE.slowDown } = o
    const [x, y] = OFFSET[from]
    const move = reduced() ? 0 : distance
    return el.animate(
        [
            { opacity: 0, transform: `translate(${x * move}px, ${y * move}px)` },
            { opacity: 1, transform: "translate(0, 0)" },
        ],
        { duration, delay, easing, fill: "backwards" }
    )
}

/** A list in, one after another. */
export function stagger(els: ArrayLike<Element>, o: EnterOptions & { gap?: number } = {}) {
    const { gap = MS.stagger, delay = 0, ...rest } = o
    return Array.from(els).map((el, i) => enter(el, { ...rest, delay: delay + i * gap }))
}

/** A list out, in the same order it came in (mirrors the entrance). */
export function leave(els: ArrayLike<Element>, o: { to?: Direction; gap?: number; distance?: number } = {}) {
    const { to = "left", gap = MS.staggerOut, distance = 16 } = o
    const [x, y] = OFFSET[to]
    const move = reduced() ? 0 : distance
    const list = Array.from(els).map((el, i) =>
        el.animate(
            [
                { opacity: 1, transform: "translate(0,0)" },
                { opacity: 0, transform: `translate(${-x * move}px, ${-y * move}px)` },
            ],
            { duration: MS.exit, delay: i * gap, easing: EASE.exit, fill: "forwards" }
        )
    )
    return Promise.all(list.map((a) => a.finished.catch(() => {})))
}

/** "A click to close always wins": stop whatever is running on el right where
 *  it is, so the next animation leaves from there. */
export function freeze(el: Element) {
    for (const a of el.getAnimations()) {
        try {
            a.commitStyles()
        } catch {}
        a.cancel()
    }
}

/** Text in line by line (per visual line, as the portfolio's headlines).
 *  Wraps each word in a span, groups them by their line on screen, and
 *  reveals the lines with a mask from below. */
export function linesIn(el: HTMLElement, o: { delay?: number; gap?: number; duration?: number } = {}) {
    const { delay = 0, gap = MS.staggerLines, duration = MS.drift } = o
    if (!el.dataset.split) {
        const words = (el.textContent || "").split(/(\s+)/)
        el.textContent = ""
        for (const w of words) {
            if (/^\s+$/.test(w)) el.append(w)
            else if (w) {
                const s = document.createElement("span")
                s.className = "nto-word"
                s.textContent = w
                el.append(s)
            }
        }
        el.dataset.split = "1"
    }
    const words = Array.from(el.querySelectorAll<HTMLElement>(".nto-word"))
    const lines: HTMLElement[][] = []
    let top = -Infinity
    for (const w of words) {
        const t = w.offsetTop
        if (Math.abs(t - top) > 2) {
            lines.push([])
            top = t
        }
        lines[lines.length - 1].push(w)
    }
    const r = reduced()
    return lines.flatMap((line, i) =>
        line.map((w) =>
            w.animate(
                r
                    ? [{ opacity: 0 }, { opacity: 1 }]
                    : [
                          { opacity: 0, transform: "translateY(0.9em)", clipPath: "inset(0 0 100% 0)" },
                          { opacity: 1, transform: "translateY(0)", clipPath: "inset(0 0 -10% 0)" },
                      ],
                { duration, delay: delay + i * gap, easing: EASE.slowDown, fill: "backwards" }
            )
        )
    )
}
